/* ==========================================================================
   nma-galeria.js — galería de entrada
   Ref: bactoboost-spec-desarrollo-v2.2.md §6.3

   Va aparte de nma-carousel.js porque no comparten nada: el carrusel es
   scroll-snap con desplazamiento nativo, la galería es un índice con
   diapositivas apiladas. Mezclarlos hubiera obligado a un modo dentro del otro.

   Sin librerías (regla 1.4).
   ========================================================================== */

/* Mismo blindaje que nma-carousel.js: si alguna vez otra sección carga este
   archivo, la segunda ejecución no debe lanzar una redeclaración. */
(() => {
  if (customElements.get('nma-galeria')) return;

  class NmaGaleria extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length === 0) return;

      this.puntos = Array.from(this.querySelectorAll('[data-punto]'));
      this.indice = 0;
      this.temporizador = null;

      this.autoplay = this.dataset.autoplay === 'true';
      this.intervalo = parseInt(this.dataset.intervalo, 10) || 6500;

      this.querySelector('[data-prev]')?.addEventListener('click', () => {
        this.ir(-1);
        this.detener();
      });
      this.querySelector('[data-next]')?.addEventListener('click', () => {
        this.ir(1);
        this.detener();
      });

      this.puntos.forEach((punto, i) => {
        punto.addEventListener('click', () => {
          this.mostrar(i);
          this.detener();
        });
      });

      this.conectarGesto();

      // Pausa al pasar el mouse o al tocar: si alguien está leyendo la
      // diapositiva, cambiársela es hostil.
      this.addEventListener('mouseenter', () => this.pausar());
      this.addEventListener('mouseleave', () => this.arrancar());
      this.addEventListener('touchstart', () => this.detener(), { passive: true });

      // Sin autoplay mientras la pestaña está oculta: no sirve de nada y gasta.
      this.onVisibilidad = () => (document.hidden ? this.pausar() : this.arrancar());
      document.addEventListener('visibilitychange', this.onVisibilidad);

      this.mostrar(0);
      this.arrancar();
    }

    disconnectedCallback() {
      this.pausar();
      document.removeEventListener('visibilitychange', this.onVisibilidad);
    }

    get prefiereMenosMovimiento() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    mostrar(i) {
      this.indice = (i + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, n) => {
        const activa = n === this.indice;
        slide.classList.toggle('is-activa', activa);
        // aria-hidden y no solo la clase: el lector de pantalla no debe leer
        // el texto de las diapositivas que no se ven.
        slide.setAttribute('aria-hidden', activa ? 'false' : 'true');
        slide.querySelectorAll('a').forEach((a) => {
          if (activa) {
            a.removeAttribute('tabindex');
          } else {
            a.setAttribute('tabindex', '-1');
          }
        });
      });

      this.puntos.forEach((punto, n) => {
        const activo = n === this.indice;
        punto.classList.toggle('is-activo', activo);
        punto.setAttribute('aria-current', activo ? 'true' : 'false');
      });
    }

    ir(dir) {
      this.mostrar(this.indice + dir);
    }

    arrancar() {
      // prefers-reduced-motion apaga el autoplay entero, no solo la transición.
      if (!this.autoplay || this.prefiereMenosMovimiento || this.slides.length < 2) return;
      this.pausar();
      this.temporizador = setInterval(() => this.ir(1), this.intervalo);
    }

    pausar() {
      if (this.temporizador) {
        clearInterval(this.temporizador);
        this.temporizador = null;
      }
    }

    /* detener = pausa definitiva. Si el visitante tomó el control con un clic o
       un gesto, el autoplay no vuelve a arrancarle la diapositiva de las manos. */
    detener() {
      this.autoplay = false;
      this.pausar();
    }

    conectarGesto() {
      let tx = 0;
      let ty = 0;

      this.addEventListener(
        'touchstart',
        (e) => {
          tx = e.changedTouches[0].clientX;
          ty = e.changedTouches[0].clientY;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchend',
        (e) => {
          const dx = e.changedTouches[0].clientX - tx;
          const dy = e.changedTouches[0].clientY - ty;
          // Umbral de 44px y detección de eje: sin comparar dx contra dy, un
          // scroll vertical con algo de diagonal cambia la diapositiva sola.
          if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) {
            this.ir(dx < 0 ? 1 : -1);
          }
        },
        { passive: true }
      );
    }
  }

  customElements.define('nma-galeria', NmaGaleria);
})();
